<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from "vue";

import ConfirmDialog from "@/components/base/ConfirmDialog.vue";
import BaseSheet from "@/components/base/BaseSheet.vue";
import VoicePreview from "@/components/lightbox/VoicePreview.vue";
import { useAudioRecorder, type RecorderError } from "@/composables/useAudioRecorder";
import { formatClock, formatDuration, pluralize } from "@/lib/format";
import { MOMENT_LABELS, type MomentId } from "@/types/album";
import { useGuestIdentityStore } from "@/stores/guestIdentity";
import { useGuestbookStore } from "@/stores/guestbook";
import { useNotificationsStore } from "@/stores/notifications";

/**
 * Port du dialogue `#voiceDialog` de la maquette : livre d'or vocal.
 *
 * Le dictaphone de la maquette d'origine était un faux (compteur qui
 * s'incrémente, barres animées au hasard). Ici l'enregistrement est réel,
 * porté par `useAudioRecorder`, et le cycle complet prêt → enregistrement →
 * relecture → envoi est implémenté, avec la liste des messages déjà
 * déposés et leur suppression.
 */

const props = defineProps<{ open: boolean }>();

const emit = defineEmits<{ "update:open": [value: boolean] }>();

const guestbook = useGuestbookStore();
const guestIdentity = useGuestIdentityStore();
const notifications = useNotificationsStore();

const {
  isRecording,
  elapsedMs,
  levels,
  error: recorderError,
  result: recordingResult,
  start: startRecording,
  stop: stopRecording,
  cancel: cancelRecording,
  reset: resetRecorder,
} = useAudioRecorder();

type Phase = "ready" | "recording" | "playback";

const phase = computed<Phase>(() => {
  if (recordingResult.value) return "playback";
  if (isRecording.value) return "recording";
  return "ready";
});

const RECORDER_ERROR_MESSAGES: Record<RecorderError, string> = {
  denied:
    "L’accès au micro a été refusé. Autorisez-le dans les réglages de votre navigateur pour laisser un message.",
  "not-found": "Aucun micro détecté sur cet appareil.",
  "insecure-context": "Le micro nécessite une connexion sécurisée (https).",
  unsupported: "Votre navigateur ne permet pas l’enregistrement audio.",
  unknown: "Une erreur inattendue est survenue. Réessayez.",
};

const recorderErrorMessage = computed(() =>
  recorderError.value ? RECORDER_ERROR_MESSAGES[recorderError.value] : null,
);

const statusLabel = computed(() => {
  if (phase.value === "recording") return "Enregistrement en cours";
  if (phase.value === "playback") return "Message prêt";
  return "Prêt à enregistrer";
});

const displayedMs = computed(() => {
  if (phase.value === "recording") return elapsedMs.value;
  if (phase.value === "playback") return recordingResult.value?.durationMs ?? 0;
  return 0;
});

const barHeight = (level: number): number => Math.max(4, Math.min(1, level) * 40);

const onRecordButtonClick = (): void => {
  if (phase.value === "recording") {
    void stopRecording();
  } else {
    void startRecording();
  }
};

/** Plafond de 120 s : le composable coupe seul, on avertit simplement la personne. */
watch(recordingResult, (result) => {
  if (result && result.durationMs >= 120_000) {
    notifications.notify("Votre message a atteint la durée maximale de 2 minutes.");
  }
});

// --- Relecture du message tout juste enregistré ---------------------------

const previewAudioEl = ref<HTMLAudioElement | null>(null);
const previewUrl = ref<string | null>(null);
const isPreviewPlaying = ref(false);
const previewCurrentMs = ref(0);

watch(recordingResult, (result) => {
  if (previewUrl.value) {
    URL.revokeObjectURL(previewUrl.value);
    previewUrl.value = null;
  }
  isPreviewPlaying.value = false;
  previewCurrentMs.value = 0;
  if (result) {
    previewUrl.value = URL.createObjectURL(result.blob);
  }
});

const previewProgress = computed(() => {
  const total = recordingResult.value?.durationMs ?? 0;
  if (total <= 0) return 0;
  return Math.min(100, (previewCurrentMs.value / total) * 100);
});

/**
 * Message actuellement ouvert dans la visionneuse. Déclaré ici, avant la
 * relecture du dictaphone, parce que les deux lectures s'excluent.
 */
const openNoteId = ref<string | null>(null);

/** Referme la visionneuse d'un message, qui coupe sa lecture au passage. */
const closeNotePreview = (): void => {
  openNoteId.value = null;
};

const togglePreviewPlayback = (): void => {
  const audio = previewAudioEl.value;
  if (!audio) return;
  if (isPreviewPlaying.value) {
    audio.pause();
    return;
  }
  closeNotePreview();
  audio.play().catch(() => {
    // La lecture peut être refusée par le navigateur (rare, hors geste
    // utilisateur) ; l'interface reste dans un état cohérent sans elle.
  });
};

const onPreviewTimeUpdate = (): void => {
  previewCurrentMs.value = (previewAudioEl.value?.currentTime ?? 0) * 1000;
};

const onPreviewEnded = (): void => {
  isPreviewPlaying.value = false;
  previewCurrentMs.value = 0;
  if (previewAudioEl.value) previewAudioEl.value.currentTime = 0;
};

const restart = (): void => {
  previewAudioEl.value?.pause();
  resetRecorder();
};

// --- Envoi du message -------------------------------------------------

const isSending = ref(false);

/**
 * Un message vocal se décrit comme une photo : qui parle, et à quel moment de
 * la journée. Les deux champs reprennent la mémoire d'`UploadSheet`, pour que
 * l'invité ne resaisisse pas son prénom d'une contribution à l'autre.
 */
const guestName = ref(guestIdentity.name);
const moment = ref<MomentId | "">(guestIdentity.lastMoment ?? "");

const momentOptions = (Object.keys(MOMENT_LABELS) as MomentId[]).map((id) => ({
  id,
  label: MOMENT_LABELS[id],
}));

watch(guestName, (value) => guestIdentity.setName(value));
// Le choix n'est mémorisé qu'une fois réellement fait : réécrire une valeur
// vide effacerait le moment retenu de la visite précédente.
watch(moment, (value) => {
  if (value) guestIdentity.setLastMoment(value);
});

const author = computed(() => guestName.value.trim() || "Un invité");

const send = async (): Promise<void> => {
  const current = recordingResult.value;
  if (!current || isSending.value) return;
  isSending.value = true;
  try {
    // `addNote` ne rejette jamais : en cas d'échec de persistance, le store
    // du livre d'or affiche déjà lui-même la notification d'erreur qui
    // convient. On ne doit donc pas la recouvrir ici par un faux succès.
    await guestbook.addNote({
      blob: current.blob,
      durationMs: current.durationMs,
      author: author.value,
      moment: moment.value || undefined,
    });
    notifications.notify("Votre message est enregistré sur cet appareil.", "success");
    restart();
  } finally {
    isSending.value = false;
  }
};

const onSendClick = (): void => {
  void send();
};

// --- Liste des messages déjà déposés -----------------------------------

// `pluralize` renvoie déjà « 3 messages » : ne pas préfixer le compte à nouveau.
const notesCountLabel = computed(() => pluralize(guestbook.notes.length, "message", "messages"));

/**
 * Un message de la liste s'ouvre dans la même visionneuse que depuis la
 * galerie, plutôt que de se lire en ligne : un seul endroit où l'on écoute,
 * avec la progression et la suppression au même endroit.
 */
const openNote = computed(
  () => guestbook.notes.find((note) => note.id === openNoteId.value) ?? null,
);

const onOpenNoteClick = (id: string): void => {
  previewAudioEl.value?.pause();
  openNoteId.value = id;
};

const pendingDeleteNoteId = ref<string | null>(null);

const cancelDeleteNote = (): void => {
  pendingDeleteNoteId.value = null;
};

const onDeleteDialogOpenChange = (open: boolean): void => {
  if (!open) pendingDeleteNoteId.value = null;
};

const confirmDeleteNote = async (): Promise<void> => {
  const id = pendingDeleteNoteId.value;
  pendingDeleteNoteId.value = null;
  if (!id) return;
  openNoteId.value = null;
  await guestbook.removeNote(id);
  notifications.notify("Le message a été supprimé de cet appareil.", "success");
};

const onConfirmDeleteNote = (): void => {
  void confirmDeleteNote();
};

// --- Ouverture / fermeture de la feuille --------------------------------

const onSheetUpdateOpen = (value: boolean): void => emit("update:open", value);

const onSheetClosed = (): void => {
  cancelRecording();
  previewAudioEl.value?.pause();
  closeNotePreview();
  // Une relecture non envoyée ne survit pas à la fermeture de la feuille :
  // rouvrir le livre d'or repart toujours d'un dictaphone prêt.
  resetRecorder();
};

onBeforeUnmount(() => {
  if (previewUrl.value) URL.revokeObjectURL(previewUrl.value);
});
</script>

<template>
  <BaseSheet
    kicker="Le livre d’or"
    title="Dites-leur"
    data-testid="voice-sheet"
    :open="props.open"
    @update:open="onSheetUpdateOpen"
    @closed="onSheetClosed"
  >
    <div class="voice-copy">
      <p>
        Un conseil, un fou rire, une promesse. Ils réécouteront ces voix bien après que les photos
        auront pâli.
      </p>
    </div>

    <p v-if="recorderErrorMessage" class="voice-error" data-testid="voice-error" role="alert">
      {{ recorderErrorMessage }}
    </p>

    <div class="recorder">
      <div
        class="waveform"
        data-testid="waveform"
        :class="{ 'is-recording': phase === 'recording' }"
        aria-hidden="true"
      >
        <i
          v-for="(level, index) in levels"
          :key="index"
          :style="phase === 'recording' ? { height: `${barHeight(level)}px` } : undefined"
        ></i>
      </div>
      <strong data-testid="record-time">{{ formatDuration(displayedMs) }}</strong>
      <span data-testid="record-status">{{ statusLabel }}</span>

      <template v-if="phase !== 'playback'">
        <button
          type="button"
          class="record-button"
          data-testid="record-button"
          :class="{ 'is-recording': phase === 'recording' }"
          :aria-label="
            phase === 'recording' ? 'Arrêter l’enregistrement' : 'Démarrer l’enregistrement'
          "
          @click="onRecordButtonClick"
        >
          <span></span>
        </button>
        <small>{{
          phase === "recording" ? "Appuyez pour arrêter" : "Appuyez pour commencer"
        }}</small>
      </template>

      <div v-else class="voice-playback">
        <audio
          ref="previewAudioEl"
          :src="previewUrl ?? undefined"
          @play="isPreviewPlaying = true"
          @pause="isPreviewPlaying = false"
          @timeupdate="onPreviewTimeUpdate"
          @ended="onPreviewEnded"
        ></audio>
        <button
          type="button"
          class="voice-playback__toggle"
          data-testid="playback-toggle"
          :aria-label="isPreviewPlaying ? 'Mettre en pause' : 'Écouter la relecture'"
          @click="togglePreviewPlayback"
        >
          <svg v-if="!isPreviewPlaying" viewBox="0 0 24 24" aria-hidden="true">
            <path d="m9 7 8 5-8 5V7Z"></path>
          </svg>
          <svg v-else viewBox="0 0 24 24" aria-hidden="true">
            <path d="M8 6h3v12H8zM13 6h3v12h-3z"></path>
          </svg>
        </button>
        <div class="voice-playback__progress">
          <div class="voice-playback__bar" :style="{ width: `${previewProgress}%` }"></div>
        </div>
        <button
          type="button"
          class="voice-playback__restart"
          data-testid="restart-recording"
          @click="restart"
        >
          Recommencer
        </button>
      </div>
    </div>

    <p class="form-note">Rien n’est obligatoire.</p>

    <div class="voice-form">
      <label>
        <span>Votre prénom</span>
        <input
          v-model="guestName"
          type="text"
          placeholder="Ex. Thomas"
          maxlength="30"
          data-testid="voice-author"
        />
      </label>
      <label>
        <span>Moment de la journée</span>
        <select v-model="moment" data-testid="voice-moment">
          <option value="">Sans précision</option>
          <option v-for="option in momentOptions" :key="option.id" :value="option.id">
            {{ option.label }}
          </option>
        </select>
      </label>
    </div>

    <button
      type="button"
      class="save-recording"
      data-testid="save-recording"
      :disabled="phase !== 'playback' || isSending"
      @click="onSendClick"
    >
      Déposer ma voix
    </button>

    <section
      v-if="guestbook.notes.length"
      class="voice-list"
      aria-label="Messages déjà enregistrés"
    >
      <div class="voice-list__heading">
        <span>Livre d’or</span>
        <small>{{ notesCountLabel }}</small>
      </div>
      <ul data-testid="note-list">
        <li v-for="note in guestbook.notes" :key="note.id" :data-note-id="note.id">
          <button
            type="button"
            class="voice-list__item"
            data-testid="note-item"
            :aria-label="`Ouvrir le message de ${note.author}`"
            @click="onOpenNoteClick(note.id)"
          >
            <span class="voice-list__play" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="m9 7 8 5-8 5V7Z"></path>
              </svg>
            </span>
            <span class="voice-list__meta">
              <strong>{{ note.author }}</strong>
              <span
                ><template v-if="note.moment">{{ MOMENT_LABELS[note.moment] }} · </template
                >{{ formatDuration(note.durationMs) }} · {{ formatClock(note.createdAt) }}</span
              >
            </span>
          </button>
        </li>
      </ul>
    </section>
  </BaseSheet>

  <VoicePreview
    :note="openNote"
    @close="openNoteId = null"
    @delete="(id) => (pendingDeleteNoteId = id)"
  />

  <ConfirmDialog
    :open="pendingDeleteNoteId !== null"
    title="Supprimer ce message ?"
    message="Cet enregistrement sera définitivement retiré de cet appareil."
    tone="danger"
    @update:open="onDeleteDialogOpenChange"
    @confirm="onConfirmDeleteNote"
    @cancel="cancelDeleteNote"
  />
</template>

<style scoped>
.form-note {
  margin: 16px 0 -6px 2px;
  color: var(--color-muted);
  font-size: 13px;
}

.voice-form {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 16px;
}

.voice-form label > span {
  display: block;
  margin: 0 0 6px 2px;
  color: var(--color-muted);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.voice-form input,
.voice-form select {
  width: 100%;
  padding: 12px;
  border: 1px solid var(--color-line);
  border-radius: 10px;
  outline: none;
  background: var(--color-paper);
  font-size: 14px;
}

.voice-form input:focus,
.voice-form select:focus {
  border-color: var(--color-rose);
}

.voice-copy {
  max-width: 430px;
  margin: 0 auto;
  color: var(--color-muted);
  font-family: var(--font-serif);
  font-size: 15px;
  line-height: 1.6;
  text-align: center;
}

.voice-error {
  max-width: 430px;
  margin: 12px auto 0;
  padding: 10px 14px;
  color: var(--color-rose-strong);
  border: 1px solid rgb(212 94 84 / 0.28);
  border-radius: 12px;
  background: rgb(212 94 84 / 0.08);
  font-size: 13px;
  line-height: 1.5;
  text-align: center;
}

.recorder {
  display: flex;
  align-items: center;
  flex-direction: column;
  margin: 25px 0 15px;
  padding: 24px 16px;
  border-radius: 18px;
  background: var(--color-paper);
}

.waveform {
  display: flex;
  align-items: center;
  gap: 3px;
  height: 42px;
  margin-bottom: 10px;
}

.waveform i {
  width: 2px;
  height: 8px;
  border-radius: 99px;
  background: var(--color-lavender);
  transition: height 0.18s ease;
}

.waveform i:nth-child(3n) {
  height: 22px;
}

.waveform i:nth-child(4n) {
  height: 31px;
}

/* Les niveaux réels du micro pilotent la hauteur via le style inline :
   l'animation au hasard de la maquette d'origine est désactivée ici. */
.waveform.is-recording i {
  animation: none;
}

.recorder > strong {
  font-family: var(--font-serif);
  font-size: 24px;
  font-weight: 400;
}

.recorder > span {
  margin-top: 3px;
  color: var(--color-muted);
  font-size: 12px;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.record-button {
  display: grid;
  place-items: center;
  width: 70px;
  height: 70px;
  margin: 20px 0 8px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: linear-gradient(145deg, var(--color-rose), var(--color-orange));
  box-shadow: 0 0 0 9px rgb(212 94 84 / 0.11);
}

.record-button span {
  width: 23px;
  height: 23px;
  border: 2px solid white;
  border-radius: 50%;
}

.record-button.is-recording span {
  width: 19px;
  height: 19px;
  border: 0;
  border-radius: 4px;
  background: white;
}

.recorder > small {
  color: var(--color-muted);
  font-size: 12px;
}

.save-recording {
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: 15px;
  padding: 15px 18px;
  color: white;
  border: 0;
  border-radius: 12px;
  background: var(--color-sage-dark);
  font-family: var(--font-script);
  font-size: 24px;
}

.save-recording:disabled {
  cursor: not-allowed;
  opacity: 0.35;
}

.voice-playback {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  margin: 20px 0 8px;
}

.voice-playback audio {
  display: none;
}

.voice-playback__toggle {
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  width: 40px;
  height: 40px;
  padding: 0;
  color: white;
  border: 0;
  border-radius: 50%;
  background: linear-gradient(145deg, var(--color-rose), var(--color-orange));
}

.voice-playback__toggle svg {
  width: 15px;
  fill: white;
}

.voice-playback__progress {
  flex: 1 1 auto;
  height: 4px;
  border-radius: 99px;
  background: var(--color-paper-2);
  overflow: hidden;
}

.voice-playback__bar {
  height: 100%;
  background: var(--color-olive);
  transition: width 0.12s linear;
}

.voice-playback__restart {
  flex: 0 0 auto;
  padding: 6px 10px;
  color: var(--color-rose-strong);
  border: 1px solid rgb(183 114 101 / 0.27);
  border-radius: 99px;
  background: transparent;
  font-size: 12px;
  font-weight: 600;
}

.voice-list {
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid var(--color-line);
}

.voice-list__heading {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.voice-list__heading span {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.11em;
  text-transform: uppercase;
}

.voice-list__heading small {
  color: var(--color-muted);
  font-size: 12px;
}

.voice-list ul {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
}

/* Toute la ligne est cliquable : elle ouvre la visionneuse du message. */
.voice-list__item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  border: 1px solid transparent;
  border-radius: 12px;
  background: var(--color-paper);
  text-align: left;
  transition: border-color 0.2s ease;
}

.voice-list__item:hover,
.voice-list__item:focus-visible {
  border-color: var(--color-line);
}

.voice-list__play {
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  width: 30px;
  height: 30px;
  padding: 0;
  color: var(--color-olive);
  border: 1px solid var(--color-line);
  border-radius: 50%;
  background: var(--color-ivory);
}

.voice-list__play svg {
  width: 11px;
  fill: currentColor;
}

.voice-list__meta {
  flex: 1 1 auto;
  min-width: 0;
}

.voice-list__meta strong {
  display: block;
  font-size: 13px;
  font-weight: 600;
}

.voice-list__meta span {
  display: block;
  margin-top: 2px;
  color: var(--color-muted);
  font-size: 12px;
}
</style>
